/**
 * IMAP Client Service
 * Connects to the imap-server via backend gateway
 */

import { getApiEndpoints } from '../config';

// Get API base from centralized config (routed through gateway)
const getApiBase = () => getApiEndpoints().imap;

// Auth: user JWT token (gateway validates and routes to IMAP service)
function getAuthHeaders(): HeadersInit {
  try {
    const authData = localStorage.getItem('auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      const token = parsed.accessToken || parsed.token;
      if (token) return { Authorization: `Bearer ${token}` };
    }
  } catch { /* ignore */ }
  return {};
}

/**
 * Build API path: account-scoped when accountId provided, legacy otherwise
 */
function buildPath(path: string, accountId?: string): string {
  if (accountId) {
    return `${getApiBase()}/${accountId}${path}`;
  }
  return `${getApiBase()}${path}`;
}

// Types
export interface ImapFolder {
  name: string;
  path: string;
  label: string;
  specialUse?: string;
  messagesTotal: number;
  messagesUnread: number;
}

export interface EmailAddress {
  name?: string;
  address: string;
}

export interface ImapMessage {
  uid: number;
  subject: string;
  from: EmailAddress | null;
  to: EmailAddress[];
  date: string;
  flags: string[];
  hasAttachments: boolean;
  snippet: string;
}

export interface ImapMessageFull extends ImapMessage {
  cc: EmailAddress[];
  body: string;
  bodyHtml: string | null;
  attachments: Array<{
    filename: string;
    contentType: string;
    size: number;
  }>;
}

export interface ImapStatus {
  imap: {
    connected: boolean;
    user?: string;
    host?: string;
  };
  smtp: {
    connected: boolean;
    user?: string;
    host?: string;
  };
}

export interface SendEmailParams {
  to: string;
  subject: string;
  body: string;
  html?: string;
  cc?: string;
  bcc?: string;
  replyTo?: string;
}

// API Functions

/**
 * Get IMAP/SMTP connection status
 */
export async function getImapStatus(accountId?: string): Promise<ImapStatus> {
  const res = await fetch(buildPath('/status', accountId), { headers: getAuthHeaders() });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return { imap: data.imap, smtp: data.smtp };
}

/**
 * Send IMAP/SMTP configuration to the server
 */
export interface ImapSmtpConfig {
  imap: {
    host: string;
    port?: number;
    user: string;
    password: string;
    tls?: boolean;
  };
  smtp: {
    host: string;
    port?: number;
    user: string;
    password: string;
    secure?: boolean;
  };
}

export async function sendImapConfig(config: ImapSmtpConfig, accountId?: string): Promise<void> {
  const res = await fetch(buildPath('/config', accountId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(config),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Connect to IMAP and SMTP servers
 */
export async function connectImap(config?: ImapSmtpConfig, accountId?: string): Promise<void> {
  const url = accountId
    ? `${getApiBase()}/${accountId}/connect`
    : `${getApiBase()}/connect`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: config ? JSON.stringify(config) : '{}',
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * List all mailbox folders
 */
export async function listFolders(accountId?: string): Promise<ImapFolder[]> {
  const res = await fetch(buildPath('/folders', accountId), { headers: getAuthHeaders() });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.folders;
}

/**
 * List messages from a folder
 */
export async function listMessages(
  folder: string = 'INBOX',
  limit: number = 50,
  offset: number = 0,
  accountId?: string
): Promise<ImapMessage[]> {
  const params = new URLSearchParams({
    folder,
    limit: String(limit),
    offset: String(offset),
  });
  const res = await fetch(`${buildPath('/messages', accountId)}?${params}`, { headers: getAuthHeaders() });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.messages;
}

/**
 * List only UNSEEN (unread) messages from a folder
 */
export async function listUnseenMessages(
  folder: string = 'INBOX',
  limit: number = 100,
  accountId?: string
): Promise<ImapMessage[]> {
  const params = new URLSearchParams({
    folder,
    limit: String(limit),
  });
  const res = await fetch(`${buildPath('/messages/unseen', accountId)}?${params}`, { headers: getAuthHeaders() });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.messages;
}

/**
 * Get a single message with full body
 */
export async function getMessage(
  uid: number,
  folder: string = 'INBOX',
  accountId?: string
): Promise<ImapMessageFull> {
  const params = new URLSearchParams({ folder });
  const res = await fetch(`${buildPath(`/message/${uid}`, accountId)}?${params}`, { headers: getAuthHeaders() });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return data.message;
}

/**
 * Send an email via SMTP
 */
export async function sendEmail(params: SendEmailParams, accountId?: string): Promise<{ messageId: string }> {
  const res = await fetch(buildPath('/send', accountId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
  return { messageId: data.messageId };
}

// =============================================================================
// FOLDER MANAGEMENT
// =============================================================================

/**
 * Create a new folder
 */
export async function createFolder(path: string, accountId?: string): Promise<void> {
  const res = await fetch(buildPath('/folders', accountId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ path }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Rename a folder
 */
export async function renameFolder(oldPath: string, newPath: string, accountId?: string): Promise<void> {
  const res = await fetch(buildPath('/folders', accountId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ oldPath, newPath }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Delete a folder
 */
export async function deleteFolder(path: string, accountId?: string): Promise<void> {
  const res = await fetch(buildPath('/folders', accountId), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ path }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

// =============================================================================
// MESSAGE MANAGEMENT
// =============================================================================

/**
 * Move a message to another folder
 */
export async function moveMessage(uid: number, fromFolder: string, toFolder: string, accountId?: string): Promise<void> {
  const res = await fetch(buildPath('/messages/move', accountId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ uid, fromFolder, toFolder }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Copy a message to another folder
 */
export async function copyMessage(uid: number, fromFolder: string, toFolder: string, accountId?: string): Promise<void> {
  const res = await fetch(buildPath('/messages/copy', accountId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ uid, fromFolder, toFolder }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Delete a message
 */
export async function deleteMessage(uid: number, folder: string, accountId?: string): Promise<void> {
  const params = new URLSearchParams({ folder });
  const res = await fetch(`${buildPath(`/messages/${uid}`, accountId)}?${params}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Mark message as read or unread
 */
export async function markMessageRead(uid: number, folder: string, read: boolean, accountId?: string): Promise<void> {
  const params = new URLSearchParams({ folder });
  const res = await fetch(`${buildPath(`/messages/${uid}/read`, accountId)}?${params}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ read }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Flag or unflag a message
 */
export async function flagMessage(uid: number, folder: string, flagged: boolean, accountId?: string): Promise<void> {
  const params = new URLSearchParams({ folder });
  const res = await fetch(`${buildPath(`/messages/${uid}/flag`, accountId)}?${params}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ flagged }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error);
}

/**
 * Helper: Check if connected, attempt connection if not
 */
export async function ensureConnected(accountId?: string): Promise<boolean> {
  try {
    const status = await getImapStatus(accountId);
    if (status.imap.connected) return true;

    await connectImap(undefined, accountId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Helper: Get common folder paths
 */
export function getStandardFolderPath(folders: ImapFolder[], type: 'inbox' | 'sent' | 'drafts' | 'trash'): string {
  const specialUseMap: Record<string, string> = {
    inbox: '\\Inbox',
    sent: '\\Sent',
    drafts: '\\Drafts',
    trash: '\\Trash',
  };

  // First try to find by special-use
  const bySpecialUse = folders.find(f => f.specialUse === specialUseMap[type]);
  if (bySpecialUse) return bySpecialUse.path;

  // Fallback to common names
  const namePatterns: Record<string, RegExp> = {
    inbox: /^inbox$/i,
    sent: /^(sent|sent items|sent mail|posta inviata)$/i,
    drafts: /^(drafts|bozze)$/i,
    trash: /^(trash|deleted|cestino)$/i,
  };

  const byName = folders.find(f => namePatterns[type].test(f.name));
  if (byName) return byName.path;

  // Default fallback
  return type === 'inbox' ? 'INBOX' : type.charAt(0).toUpperCase() + type.slice(1);
}
